import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import dayjs from '../../../utils/dayjs';
import s from './TimePicker.module.css';
import MLIcon from '../../MLIcon';

class TimePicker extends Component {
  static propTypes = {
    time: PropTypes.string,
    className: PropTypes.string,
    onChange: PropTypes.func,
    hourTestId: PropTypes.string,
    minuteTestId: PropTypes.string,
    amPmTestId: PropTypes.string,
    disabled: PropTypes.bool,
  };

  static validTwoCharStr(str) {
    return str[0] && str[0] !== undefined && str[1] && str[1] !== undefined;
  }

  static handleInvalidChar(node) {
    node.classList.add(s.error);
    setTimeout(() => {
      node.classList.remove(s.error);
    }, 200);
  }

  constructor(props) {
    super(props);
    const parsedTime = this.formatInput();
    this.state = {
      hour: parsedTime.hour,
      minute: parsedTime.minute,
      period: parsedTime.period,
    };
  }

  componentDidUpdate(_prevProps, prevState) {
    const hasChanged = !_.chain(this.state)
      .pick(['hour', 'minute', 'period'])
      .isMatch(prevState)
      .value();

    if (hasChanged) {
      this.props.onChange(this.formatOutput());
    }
  }

  setTime(timeObj) {
    let { hour, minute, period } = this.formatInput();
    hour = (timeObj && timeObj.hour) || hour;
    minute = (timeObj && timeObj.minute) || minute;
    period = (timeObj && timeObj.period) || period;

    const parsedTime = dayjs(`${hour}:${minute} ${period}`, 'hh:mm A');
    if (
      parsedTime.isValid() ||
      TimePicker.validTwoCharStr(hour) ||
      TimePicker.validTwoCharStr(minute) ||
      TimePicker.validTwoCharStr(period)
    ) {
      this.props.onChange(parsedTime.format('HH:mm'));
    }
  }

  formatInput() {
    const { time } = this.props;
    if (!time) return { hour: '11', minute: '59', period: 'PM' };

    const hour = dayjs(time, 'HH:mm').format('hh');
    const minute = dayjs(time, 'HH:mm').format('mm');
    const period = dayjs(time, 'HH:mm').format('A');

    return { hour, minute, period };
  }

  togglePeriod() {
    const { period } = this.formatInput();
    if (period === 'AM') {
      this.setTime({ period: 'PM' });
    } else {
      this.setTime({ period: 'AM' });
    }
  }

  handleHourKey(e) {
    const { hour } = this.formatInput();
    if (!TimePicker.validTwoCharStr(hour)) return;

    let nHour = parseInt(hour, 10);
    if (!Number.isFinite(nHour)) nHour = 12;
    switch (e.key) {
      case 'ArrowUp': {
        if (nHour < 12) nHour += 1;
        else nHour = 1;
        break;
      }
      case 'ArrowDown': {
        if (nHour > 1) nHour -= 1;
        else nHour = 12;
        break;
      }
      case 'ArrowLeft': {
        break;
      }
      case 'ArrowRight': {
        document.getElementById('__timePickerMinute').focus();
        break;
      }
      case 'Tab': {
        break;
      }
      default: {
        if (/^[0-9]/.test(e.key)) {
          this.handleHourChange(e.key);
          return;
        }
        TimePicker.handleInvalidChar(
          document.getElementById('__timePickerHour'),
        );
      }
    }

    if (nHour >= 10) this.setTime({ hour: `${nHour}` });
    else this.setTime({ hour: `0${nHour}` });
  }

  handleMinuteKey(e) {
    const { minute } = this.formatInput();
    if (!TimePicker.validTwoCharStr(minute)) return;

    let nMinute = parseInt(minute, 10);
    if (!Number.isFinite(nMinute)) nMinute = 0;
    switch (e.key) {
      case 'ArrowUp': {
        if (nMinute < 59) nMinute += 1;
        else nMinute = 0;
        break;
      }
      case 'ArrowDown': {
        if (nMinute > 0) nMinute -= 1;
        else nMinute = 59;
        break;
      }
      case 'ArrowLeft': {
        document.getElementById('__timePickerHour').focus();
        break;
      }
      case 'ArrowRight': {
        document.getElementById('__timePickerPeriod').focus();
        break;
      }
      case 'Tab': {
        break;
      }
      default: {
        if (/^[0-9]/.test(e.key)) {
          this.handleMinuteChange(e.key);
          return;
        }
        TimePicker.handleInvalidChar(
          document.getElementById('__timePickerMinute'),
        );
      }
    }

    if (nMinute >= 10) this.setTime({ minute: `${nMinute}` });
    else this.setTime({ minute: `0${nMinute}` });
  }

  handlePeriodKey(e) {
    const { period } = this.formatInput();
    if (!TimePicker.validTwoCharStr(period)) return;

    switch (e.key) {
      case 'ArrowUp': {
        this.togglePeriod();
        break;
      }
      case 'ArrowDown': {
        this.togglePeriod();
        break;
      }
      case 'ArrowLeft': {
        document.getElementById('__timePickerMinute').focus();
        break;
      }
      case 'ArrowRight': {
        break;
      }
      case 'Tab': {
        break;
      }
      default: {
        const uCaseKey = e.key.toUpperCase();
        if (uCaseKey === 'A' || uCaseKey === 'P') {
          this.handlePeriodChange(uCaseKey);
          return;
        }
        TimePicker.handleInvalidChar(
          document.getElementById('__timePickerPeriod'),
        );
      }
    }
  }

  handleHourChange(key) {
    const { hour } = this.formatInput();
    const intHour = parseInt(hour, 10);
    let changedHour = null;
    if (/^[3-9]/.test(key)) changedHour = `0${key}`;
    else if (intHour < 2) changedHour = `1${key}`;
    else if (key === '0') changedHour = '12';
    else changedHour = `0${key}`;
    this.setTime({ hour: changedHour });
  }

  handleMinuteChange(key) {
    const { minute } = this.formatInput();
    let changedMinute = null;
    if (/^[6-9]/.test(minute[0])) changedMinute = `0${key}`;
    else if (/^[6-9]/.test(minute[1])) changedMinute = `0${key}`;
    else changedMinute = `${minute[1]}${key}`;

    this.setTime({ minute: changedMinute });
  }

  handlePeriodChange(key) {
    this.setTime({ period: `${key}M` });
  }

  render() {
    const { hour, minute, period } = this.formatInput();
    const { hourTestId, minuteTestId, amPmTestId, disabled } = this.props;
    return (
      <div className={`${s.timeInputWrapper} ${this.props.className}`}>
        <div
          id={this.state.anchorInputId}
          className={`${s.container} ${s.timeContainer} ${
            disabled ? s.disabled : ''
          }`}
        >
          <div className={s.iconContainer}>
            <MLIcon
              className={`${s.clock} ${disabled ? s.clockDisabled : ''}`}
              type="clock"
              role="presentation"
            />
          </div>
          <div className={s.inputContainer}>
            <input
              id="__timePickerHour"
              className={s.timePickerInput}
              name="hour"
              type="text"
              maxLength="2"
              value={parseInt(hour, 10).toString()}
              onChange={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onKeyUp={(e) => {
                this.handleHourKey(e);
              }}
              onClick={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onFocus={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              disabled={disabled}
              data-test-id={hourTestId}
              autoComplete="off"
            />
            <span className={s.timePickerDivider}>:</span>
            <input
              className={s.timePickerInput}
              id="__timePickerMinute"
              name="minute"
              type="text"
              maxLength="2"
              value={minute}
              autoComplete="off"
              onChange={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onKeyUp={(e) => {
                this.handleMinuteKey(e);
              }}
              onClick={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onFocus={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              disabled={disabled}
              data-test-id={minuteTestId}
            />
            <input
              className={s.timePickerInputPeriod}
              id="__timePickerPeriod"
              value={period}
              type="text"
              maxLength="2"
              onChange={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onKeyUp={(e) => {
                this.handlePeriodKey(e);
              }}
              onClick={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              onFocus={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
              }}
              disabled={disabled}
              data-test-id={amPmTestId}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default TimePicker;
